import type { BreakEventRequest } from "@/shared/repositories/CalendarEventRepository";
import type { PlanChangeRef } from "@/shared/repositories/PlanChangeRepository";
import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { ExpandMore, WarningAmber } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	LinearProgress,
	Typography,
} from "@mui/material";
import { useBreakPlanConfirmation } from "../hooks/useBreakPlanConfirmation";
import { formatDuration, formatTimeRange } from "../utils/calendarUtils";
import { getEventType } from "../config/eventTypes";
import {
	ConfirmEventChangeModal,
	EventChangeNotice,
} from "./ConfirmEventChangeModal";
import { WideSummaryCard } from "./EventSummaryCard";

const moment = getMomentTz();
const windowLabel = (start: string | null, end: string | null) => {
	if (!start || !end) return "New event";
	return formatTimeRange(moment.utc(start).toDate(), moment.utc(end).toDate());
};

type Props = {
	request: BreakEventRequest;
	intent?: "create" | "edit" | "move";
	onSaved: (refs: PlanChangeRef[]) => void;
	onCancel: () => void;
};

export const BreakPlanConfirmationModal = ({
	request,
	intent = request.eventId == null ? "create" : "move",
	onSaved,
	onCancel,
}: Props) => {
	const {
		rows,
		saving,
		previewLoading,
		previewFailed,
		planChanged,
		error,
		clearError,
		reload,
		confirm,
	} = useBreakPlanConfirmation({ request, onSaved });
	const pushed = rows.filter((row) => !row.IsAnchor);
	const shiftChanges = rows.filter((row) => row.CrossesShift);
	const shiftUnknown = rows.some((row) => row.ShiftUnknown);
	const busy = saving || previewLoading;

	const anchors = rows.filter((row) => row.IsAnchor);
	const anchor = anchors[0];
	const details = rows.filter(
		(row) =>
			!row.IsAnchor ||
			row.CrossesShift ||
			row.ShiftUnknown ||
			anchors.length > 1,
	);
	const action =
		intent === "create" ? "Create" : intent === "edit" ? "Save" : "Move";

	return (
		<>
			<ConfirmEventChangeModal
				title={`Confirm ${action} Event`}
				actionLabel={
					intent === "create"
						? "Creating"
						: intent === "edit"
							? "Updating"
							: "Moving"
				}
				confirmLabel={`Confirm ${action}`}
				summary={
					<WideSummaryCard
						color={getEventType("B").color}
						groups={[
							[
								{
									label: "Event",
									value: anchor?.BreakLabel ?? "Break / Lunch",
								},
								...(anchors.length
									? [
											{
												label: anchors.length > 1 ? "Lines" : "Line",
												value: [
													...new Set(anchors.map((row) => row.AssetName)),
												].join(", "),
											},
										]
									: []),
							],
							[
								{
									label: "Duration",
									value: formatDuration(
										moment.utc(request.start).toDate(),
										moment.utc(request.end).toDate(),
									),
								},
							],
						]}
					/>
				}
				oldStart={
					anchor?.OldStart ? moment.utc(anchor.OldStart).toDate() : null
				}
				oldEnd={anchor?.OldEnd ? moment.utc(anchor.OldEnd).toDate() : null}
				oldPlaceholder={
					request.eventId == null
						? "New event"
						: previewLoading
							? "Loading…"
							: "Unavailable"
				}
				newStart={moment.utc(request.start).toDate()}
				newEnd={moment.utc(request.end).toDate()}
				loading={saving}
				disabled={previewLoading || previewFailed || !rows.length}
				onConfirm={confirm}
				onCancel={onCancel}
				actions={
					previewFailed ? (
						<Button onClick={reload} disabled={busy}>
							Reload preview
						</Button>
					) : undefined
				}
			>
				{previewLoading && <LinearProgress sx={{ mb: 2 }} />}
				{planChanged && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						The calendar changed. Review the updated times and confirm again.
					</Typography>
				)}
				{pushed.length > 0 && (
					<EventChangeNotice tone="warning">
						<WarningAmber fontSize="small" />
						{pushed.length} break{pushed.length > 1 ? "s" : ""} in the way will
						be pushed later
					</EventChangeNotice>
				)}
				{shiftChanges.length > 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						Shift change: {shiftChanges.length} break
						{shiftChanges.length > 1 ? "s" : ""}.
					</Typography>
				)}
				{shiftUnknown && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						Shift information is unavailable for some of these times.
					</Typography>
				)}
				{details.length > 0 && (
					<Accordion
						disableGutters
						elevation={0}
						sx={{ "&:before": { display: "none" } }}
					>
						<AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
							<Typography variant="body2">
								View affected breaks ({details.length})
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ px: 0 }}>
							{details.map((row) => (
								<Box key={`${row.AssetId}-${row.EventId}`} sx={{ mb: 1.5 }}>
									<Typography variant="body2" fontWeight={600}>
										{row.AssetName} · {row.BreakLabel}
										{row.EventId > 0 ? ` #${row.EventId}` : ""}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{windowLabel(row.OldStart, row.OldEnd)} →{" "}
										{windowLabel(row.NewStart, row.NewEnd)}
									</Typography>
									{(row.CrossesShift || row.ShiftUnknown) && (
										<Typography variant="caption" color="text.secondary">
											Shift: {row.OldShift ?? "Unavailable"} →{" "}
											{row.NewShift ?? "Unavailable"}
											{row.EndShift !== row.NewShift
												? ` → ${row.EndShift ?? "Unavailable"}`
												: ""}
										</Typography>
									)}
								</Box>
							))}
						</AccordionDetails>
					</Accordion>
				)}
			</ConfirmEventChangeModal>
			<ErrorModal error={error} onHide={clearError} />
		</>
	);
};
